﻿/**
 * Armazenamento do save do jogo no navegador, em IndexedDB (centenas de MB, assíncrono) no lugar do localStorage (uns 5 MB,
 * síncrono, trava a página em saves grandes). Guarda o JSON do save como texto, igual ao arquivo de "Exportar save".
 * Preferências pequenas (som, velocidade, tema, sessão online) continuam no localStorage.
 *
 * Se o IndexedDB não existir ou estiver bloqueado (janela anônima, dados bloqueados), cai para o localStorage como antes.
 */

const BANCO = 'novo-jogo';
const LOJA = 'saves';
const CHAVE_LOCAL = 'principal';
/** Chave antiga do localStorage, usada só como plano B quando o IndexedDB não funciona. */
const CHAVE_LOCALSTORAGE = 'novo-jogo:save:v2';

let abertura: Promise<IDBDatabase | null> | null = null;

function abrir(): Promise<IDBDatabase | null> {
  if (abertura) return abertura;
  abertura = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(BANCO, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(LOJA)) req.result.createObjectStore(LOJA);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return abertura;
}

function pedido<T>(db: IDBDatabase, modo: IDBTransactionMode, fazer: (loja: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(LOJA, modo);
    const req = fazer(tx.objectStore(LOJA));
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** Passa os bytes por um (de)compressor gzip do navegador. */
async function passarPor(bytes: BufferSource, fluxo: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const escritor = fluxo.writable.getWriter();
  void escritor.write(bytes).then(() => escritor.close());
  return new Uint8Array(await new Response(fluxo.readable).arrayBuffer());
}

/** Grava o save (texto JSON), comprimido em gzip quando o navegador sabe (uns 10x menor). Lança erro se não couber ou se o navegador bloquear. */
export async function gravarSave(texto: string): Promise<void> {
  const db = await abrir();
  if (db) {
    let valor: string | Uint8Array = texto;
    try {
      if (typeof CompressionStream !== 'undefined') valor = await passarPor(new TextEncoder().encode(texto), new CompressionStream('gzip'));
    } catch {
      valor = texto; // sem compressão: grava o texto puro, que a leitura também aceita
    }
    await pedido(db, 'readwrite', (l) => l.put(valor, CHAVE_LOCAL));
    return;
  }
  localStorage.setItem(CHAVE_LOCALSTORAGE, texto);
}

/** Lê o save (texto JSON) ou null se não houver. Aceita o formato antigo (texto puro) e o comprimido. */
export async function lerSave(): Promise<string | null> {
  try {
    const db = await abrir();
    if (db) {
      const bruto = (await pedido<unknown>(db, 'readonly', (l) => l.get(CHAVE_LOCAL))) ?? null;
      if (bruto === null || typeof bruto === 'string') return bruto;
      const bytes = bruto instanceof Uint8Array ? bruto : new Uint8Array(bruto as ArrayBuffer);
      return new TextDecoder().decode(await passarPor(bytes as BufferSource, new DecompressionStream('gzip')));
    }
    return localStorage.getItem(CHAVE_LOCALSTORAGE);
  } catch {
    return null;
  }
}

/** Apaga o save do navegador (usado quando ele está corrompido e derrubaria a tela a cada abertura). */
export async function apagarSave(): Promise<void> {
  try {
    const db = await abrir();
    if (db) await pedido(db, 'readwrite', (l) => l.delete(CHAVE_LOCAL));
    else localStorage.removeItem(CHAVE_LOCALSTORAGE);
  } catch {
    /* sem como apagar: o próximo boot tenta de novo */
  }
}

/** Pede ao navegador para não apagar os dados do jogo quando faltar espaço (ele pode recusar). */
export async function pedirPersistencia(): Promise<void> {
  try {
    if (navigator.storage?.persist) await navigator.storage.persist();
  } catch {
    /* sem suporte: segue sem */
  }
}
